import { Backend } from '../../../api/Backend';
import {
  IAssetAction,
  IProgramV5,
  IRibbon2Detail,
  IRibbon2Schedule,
  IScheduleSummary,
  IScheduleV5,
  ISeasonV5,
} from '../../../api/models/AndromeTV.types';
import { getTime } from '../../utility/DateFormatter';
import { ActionMethod, AssetType } from '../../../api/models/Shared.types';
import { EpgUtil } from '../../utility/EpgUtil';
import { CmsCardIcon } from '../../../components/cms/CmsCardIcon';
import { ChannelModel } from '../../../api/EpgApi.types';
import { Recording, RecordingIcons } from '../../../api/user/RecordingApi.types';
import { Utils } from '@lightningjs/sdk';
import { GlobalSettingsKeys } from '../../../api/models/GlobalSettingsKeys';
import { GuideTranslationKeys } from '../../../api/models/TranslationKeys';
import { AccessibilityIcons, AccessibilityTypes } from '../../AccessibilityTypes';

interface iconDefinition {
  type: typeof CmsCardIcon;
  icon: string;
}

export class EpgGuideProgramDataSource {
  public readonly backend: Backend;
  private readonly schedule: IScheduleV5;
  private readonly program?: IProgramV5;
  // private start: Date | undefined; // used for debugging
  // private end: Date | undefined; // used for debugging
  private readonly _miniDetailDataPromise: Promise<(IRibbon2Detail & { schedule: IRibbon2Schedule }) | undefined>;
  private season?: ISeasonV5;
  private _iconPromise: Promise<iconDefinition[]> | undefined;
  private readonly channelModel: ChannelModel;
  private icons: iconDefinition[] = [];
  private recording: Recording | undefined;

  constructor(
    backend: Backend,
    schedule: IScheduleV5,
    channel: ChannelModel,
    program?: IProgramV5,
    season?: ISeasonV5
  ) {
    this.backend = backend;
    this.schedule = schedule;
    this.program = program;
    this.season = season;
    this.channelModel = channel;
    this._miniDetailDataPromise = this.getMiniDetailDataPromise();
  }

  get startTime() {
    // this.start = new Date(Number(this.schedule.published.start) * 1000); // used for debugging
    return Number(this.schedule.published.start) * 1000;
  }

  get endTime() {
    // this.end = new Date(Number(this.schedule.published.end) * 1000); // used for debugging
    return Number(this.schedule.published.end) * 1000;
  }

  get programId(): string | undefined {
    return this.program?.programId;
  }

  get channelId(): string {
    return this.channelModel.channelId;
  }

  get scheduleId() {
    return this.schedule.scheduleId;
  }

  get image(): string | undefined {
    return this.program?.image.poster;
  }

  get title(): string | undefined {
    // if program is adult then hide title
    if (this.program?.adult === false) {
      return this.program?.text.title;
    }
    return this.backend.translations.forKey(GuideTranslationKeys.guide_adult_title);
  }

  get shortSynopsis(): string | undefined {
    return this.program?.text.shortDescription;
  }

  get startTimeString(): string {
    return getTime(new Date(Number(this.schedule.published.start) * 1000));
  }

  get action(): IAssetAction {
    return {
      method: ActionMethod.DETAIL,
      type: AssetType.SCHEDULE,
      id: this.scheduleId,
      extra: undefined,
    };
  }

  get miniDetailData(): Promise<(IRibbon2Detail & { schedule: IRibbon2Schedule }) | undefined> {
    return this._miniDetailDataPromise;
  }

  private getMiniDetailDataPromise(): Promise<
    | (IRibbon2Detail & {
        schedule: IScheduleSummary;
      })
    | undefined
  > {
    return this.getEpgIcons().then(icons => {
      const scheduleSummary: IScheduleSummary = {
        id: this.schedule?.scheduleId,
        published: this.schedule?.published,
        program: this.schedule?.programId,
        live: this.schedule?.live,
        blackouts: this.schedule?.blackouts,
        accessibilityOptions: this.schedule?.accessibilityOptions,
      };
      if (!this.program) return undefined;

      return {
        type: AssetType.PROGRAM,
        id: this.program.programId,
        season: this.season?.seasonNumber,
        episode: this.program.episodeNumber,
        image: this.image,
        title: this.program.text.title,
        synopsis: this.shortSynopsis,
        minimumAge: this.program.minimumAge,
        recordingIcons: icons.filter(icon => !icon.icon?.includes(RecordingIcons.locked)),
        genres: this.program.genres ?? [],
        schedule: scheduleSummary,
      };
    });
  }

  get genreStrings() {
    const arr: string[] = this.program?.categoryTags ?? [];
    return arr.map(el => el.toLowerCase());
  }

  get accessibilityOptions() {
    return this.schedule.accessibilityOptions ?? [];
  }

  _loadRecording(programId: string, channelId: string): Promise<Recording | undefined> {
    return this.backend.cachedRecordingsApi.recordingForProgram(this.backend, programId, channelId).then(recording => {
      this.recording = recording;
      return recording;
    });
  }

  getEpgIcons() {
    if (this._iconPromise) {
      return this._iconPromise;
    }
    this._iconPromise = new Promise(resolve => {
      if (this.program && this.programId && this.schedule) {
        this._addAccessibilityIcons(this.accessibilityOptions);
        return this._loadRecording(this.programId, this.channelId).then(recording => {
          if (recording) {
            this._addRecordingIcons(recording);
          }
          this._addPlayIcon(recording);
          resolve(this.icons);
        });
      } else {
        resolve(this.icons);
      }
    });
    return this._iconPromise;
  }

  _addAccessibilityIcons(tags: string[] = []) {
    // comment next line to enable icons without global setting
    if (!this.backend.globalSettings.boolValue(GlobalSettingsKeys.showAccessibilityIcons)) return;
    // manual if test checking to ensure correct order
    if (tags.includes(AccessibilityTypes.SUBTITLES)) {
      this.icons.push({
        type: CmsCardIcon,
        icon: Utils.asset(AccessibilityIcons[AccessibilityTypes.SUBTITLES]),
      });
    }
    if (tags.includes(AccessibilityTypes.AUDIO_DESCRIPTION)) {
      this.icons.push({
        type: CmsCardIcon,
        icon: Utils.asset(AccessibilityIcons[AccessibilityTypes.AUDIO_DESCRIPTION]),
      });
    }
    if (tags.includes(AccessibilityTypes.SIGN_LANGUAGE)) {
      this.icons.push({
        type: CmsCardIcon,
        icon: Utils.asset(AccessibilityIcons[AccessibilityTypes.SIGN_LANGUAGE]),
      });
    }
  }

  _addRecordingIcons(recording: Recording) {
    const recIcons = EpgUtil.getRecordingIcons(recording);
    recIcons.forEach(recIcon => {
      this.icons.push({
        type: CmsCardIcon,
        icon: recIcon.src,
      });
    });
  }

  _addPlayIcon(recording: Recording | undefined) {
    const play = EpgUtil.getPlayIcon(this.backend, this.schedule, recording, this.channelModel, undefined, false);
    if (play) {
      this.icons.push({
        type: CmsCardIcon,
        icon: Utils.asset(play),
      });
    }
  }

  get adult() {
    return this.program?.adult;
  }
}
